/**
 * Propagation d'événements d'authentification entre onglets d'une même origine.
 *
 * Sans ça, un `logout` dans un onglet reste invisible pour les autres : ils
 * continuent d'afficher une UI connectée jusqu'à leur prochain 401 — et comme
 * l'horodatage « refresh récent » est partagé, ils pouvaient même croire qu'un
 * refresh venait de réussir et rejouer indéfiniment.
 *
 * `BroadcastChannel` en premier ; repli sur l'événement `storage` (émis
 * uniquement dans les AUTRES onglets, ce qui est exactement le comportement
 * voulu) pour les navigateurs qui ne l'ont pas.
 */

export type CrossTabMessage = { type: "logout" } | { type: "refreshed"; at: number };

export interface CrossTab {
  post(message: CrossTabMessage): void;
  subscribe(handler: (message: CrossTabMessage) => void): () => void;
  close(): void;
}

export function createCrossTab(namespace: string): CrossTab {
  const channelName = `${namespace}:auth`;
  const fallbackKey = `${namespace}:auth:broadcast`;

  const channel =
    typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(channelName) : null;

  return {
    post(message) {
      if (channel) {
        try {
          channel.postMessage(message);
          return;
        } catch {
          // Canal fermé (onglet en cours de déchargement) — on tombe sur le repli.
        }
      }
      try {
        // La valeur doit changer à chaque envoi, sinon aucun événement `storage`
        // n'est émis.
        localStorage.setItem(fallbackKey, JSON.stringify({ ...message, _n: Date.now() }));
      } catch {
        // localStorage indisponible (mode privé, quota) — best-effort.
      }
    },

    subscribe(handler) {
      const onChannel = (event: MessageEvent) => {
        if (event.data && typeof event.data.type === "string") handler(event.data);
      };
      const onStorage = (event: StorageEvent) => {
        if (event.key !== fallbackKey || !event.newValue) return;
        try {
          const parsed = JSON.parse(event.newValue);
          if (parsed && typeof parsed.type === "string") handler(parsed);
        } catch {
          // Valeur illisible — on ignore.
        }
      };

      channel?.addEventListener("message", onChannel);
      if (typeof window !== "undefined") window.addEventListener("storage", onStorage);

      return () => {
        channel?.removeEventListener("message", onChannel);
        if (typeof window !== "undefined") window.removeEventListener("storage", onStorage);
      };
    },

    close() {
      try {
        channel?.close();
      } catch {
        // Déjà fermé.
      }
    },
  };
}
